import React from 'react';
import { Plus, ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface PageHeaderProps {
  totalCount: number;
  onNewContent: () => void;
  /**
   * Quick Create v2 (#1340): render "New Article" as a split-button with a mode
   * dropdown (Quick Create / Bulk Generate) instead of a plain button that opens
   * the modal chooser. Off = the original plain button.
   */
  showModeDropdown?: boolean;
  onQuickCreate?: () => void;
  onBulkGenerate?: () => void;
  /** Bulk Generate is disabled + "Coming soon" (client, or staff in client view). */
  bulkDisabled?: boolean;
}

const coralButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '9px 14px',
  background: 'var(--color-coral)',
  border: '1px solid var(--color-coral)',
  color: '#fff',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  // Pin the line box to the icon height. Preflight gives form controls
  // `line-height: inherit`, so without this the label half inherits the page's
  // 1.5 and grows ~3px taller than the icon-only chevron half — the split
  // button's two halves then no longer line up.
  lineHeight: 1,
  boxSizing: 'border-box',
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(217,119,6,0.18)',
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  totalCount,
  onNewContent,
  showModeDropdown = false,
  onQuickCreate,
  onBulkGenerate,
  bulkDisabled = false,
}) => {
  return (
    <div
      style={{
        padding: '22px 28px 18px',
        borderBottom: '1px solid var(--color-warm-border)',
        background: 'var(--color-warm-bg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 6,
            }}
          >
            <h1
              className="font-display"
              style={{
                margin: 0,
                fontSize: 30,
                fontWeight: 600,
                color: 'var(--color-ink)',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              Content Planner
            </h1>
            <span
              style={{
                padding: '2px 9px',
                borderRadius: 11,
                background: 'var(--color-paper-2)',
                color: 'var(--color-ink-2)',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: '18px',
              }}
            >
              {totalCount}
            </span>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--color-ink-2)',
              lineHeight: 1.5,
              maxWidth: 560,
            }}
          >
            Every piece of content from idea to publish — pinned priorities,
            in‑flight drafts, and the backlog you'll commit to next.
          </p>
        </div>

        {showModeDropdown ? (
          <div style={{ display: 'inline-flex', alignItems: 'stretch' }}>
            {/* Main click = the default mode (Quick Create), no chooser screen. */}
            <button
              type="button"
              onClick={onQuickCreate ?? onNewContent}
              style={{
                ...coralButton,
                borderRadius: '8px 0 0 8px',
                // Divider lives on the chevron half only — a border on both
                // halves reads as a 2px seam and offsets where each half starts.
                borderRight: 'none',
              }}
            >
              <Plus size={16} strokeWidth={2} />
              New Article
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Choose creation mode"
                  style={{
                    ...coralButton,
                    borderRadius: '0 8px 8px 0',
                    padding: '9px 8px',
                    borderLeft: '1px solid rgba(255,255,255,0.28)',
                  }}
                >
                  <ChevronDown size={16} strokeWidth={2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onQuickCreate}>Quick Create</DropdownMenuItem>
                <DropdownMenuItem
                  onClick={bulkDisabled ? undefined : onBulkGenerate}
                  disabled={bulkDisabled}
                >
                  Bulk Generate
                  {bulkDisabled && (
                    <span className="ml-2 text-[10px] text-muted-foreground">Coming soon</span>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <button
            type="button"
            onClick={onNewContent}
            style={{ ...coralButton, borderRadius: 8 }}
          >
            <Plus size={16} strokeWidth={2} />
            New Article
          </button>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
